import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Eye, EyeOff, ExternalLink, Loader2, Quote } from 'lucide-react';
import { useCreativeStrategyStore } from '@/stores/creativeStrategy';
import { creativeStrategyApi, type CreativeStrategyItem } from '@/services/api';
import { ConceptReviews } from './ConceptReviews';

const CLASS_LABEL: Record<CreativeStrategyItem['entity_class'], string> = {
  market_concept: 'buyer concept',
  creative_strategy: 'strategy',
  creative_pattern: 'template',
};

function adLibraryUrl(key: string): string | null {
  // Pack keys are "lib:<meta ad library id>"; other prefixes are not linkable.
  return key.startsWith('lib:') ? `https://www.facebook.com/ads/library/?id=${encodeURIComponent(key.slice(4))}` : null;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-medium mb-1">{label}</div>
      <div className="text-sm text-slate-800">{children}</div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm font-semibold text-navy-900 tabular-nums">{value}</div>
    </div>
  );
}

export function PackItemDetail({ clientUid }: { clientUid: string }) {
  const { selectedItem, setItemOverride, saving, citations, pack } = useCreativeStrategyStore();
  const item = selectedItem();
  const [note, setNote] = useState('');

  useEffect(() => {
    setNote(item?.note ?? '');
  }, [item?.resolved_id, item?.note]);

  if (!item) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-slate-400">
        Select an item to view its evidence
      </div>
    );
  }

  const d = item.data as Record<string, unknown>;
  const levels = (d.levels as string[] | undefined) ?? [];
  const slots = (d.slots as string[] | undefined) ?? [];
  const cited = citations?.items?.[item.resolved_id] ?? 0;
  const canManage = !!pack?.is_served_version;

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b border-slate-200">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
              <span className="font-mono">{item.resolved_id}</span>
              <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">{CLASS_LABEL[item.entity_class]}</span>
              {levels.map((l) => (
                <span key={l} className="px-1.5 py-0.5 rounded-full bg-navy-50 text-navy-700 border border-navy-200">{l}</span>
              ))}
              {item.hidden && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                  <EyeOff size={10} /> hidden from briefs
                </span>
              )}
            </div>
            <h2 className="text-lg font-semibold text-navy-900">{item.canonical_name}</h2>
          </div>
          <button
            onClick={() => setItemOverride(clientUid, item.resolved_id, !item.hidden, note)}
            disabled={saving || !canManage}
            title={canManage ? (item.hidden ? 'Offer this item to briefs and angles again' : 'Withhold this item from briefs, angles and the MCP tool') : 'Switch to the served version to manage items'}
            className={clsx(
              'inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border disabled:opacity-50 flex-shrink-0',
              item.hidden
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
            )}
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : item.hidden ? <Eye size={12} /> : <EyeOff size={12} />}
            {item.hidden ? 'Unhide' : 'Hide'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {item.entity_class === 'market_concept' ? (
            <>
              <Stat label="Review mentions" value={item.support ?? '—'} />
              <Stat label="Sentiment" value={(d.sentiment as string) ?? '—'} />
              <Stat label="Concept type" value={(d.concept_type as string) ?? '—'} />
              <Stat label="In client graph" value={item.in_client_graph ? 'yes' : 'no'} />
            </>
          ) : item.entity_class === 'creative_strategy' ? (
            <>
              <Stat label="Advertisers" value={item.advertisers ?? '—'} />
              <Stat label="Ads" value={item.support ?? '—'} />
              <Stat label="Median days running" value={item.median_days_running ?? '—'} />
              <Stat
                label="Client runs it"
                value={item.client_runs_it === null || item.client_runs_it === undefined ? 'no client ads' : item.client_runs_it ? 'yes' : 'not yet'}
              />
            </>
          ) : (
            <>
              <Stat label="Brands" value={item.advertisers ?? '—'} />
              <Stat label="Ads" value={item.support ?? '—'} />
              <Stat label="Slots" value={slots.length} />
              <Stat label="Cited by angles" value={cited} />
            </>
          )}
        </div>

        {item.entity_class === 'market_concept' && (
          <>
            {typeof d.decision_factor === 'string' && <Field label="Decision factor">{d.decision_factor}</Field>}
            {item.evidence[0] && (
              <Field label="Review evidence">
                <blockquote className="flex gap-2 text-slate-700 italic border-l-2 border-slate-200 pl-3">
                  <Quote size={14} className="text-slate-300 flex-shrink-0 mt-0.5" />
                  <span>{item.evidence[0]}</span>
                </blockquote>
              </Field>
            )}
            <Field label="Reviews behind this concept (Amazon, internal only)">
              <ConceptReviews
                load={() => creativeStrategyApi.packItemReviews(clientUid, item.resolved_id, pack?.version)}
                deps={[clientUid, item.resolved_id, pack?.version]}
              />
            </Field>
            {item.graph_concept && (
              <Field label="Matches client graph concept">
                <span className="text-emerald-700">{item.graph_concept}</span>
              </Field>
            )}
          </>
        )}

        {item.entity_class === 'creative_strategy' && (
          <>
            {typeof d.strategy_type === 'string' && <Field label="Strategy type">{d.strategy_type}</Field>}
            {typeof d.argument === 'string' && <Field label="Argument">{d.argument}</Field>}
          </>
        )}

        {item.entity_class === 'creative_pattern' && (
          <>
            {typeof d.structure === 'string' && <Field label="Structure">{d.structure}</Field>}
            {typeof d.best_for === 'string' && <Field label="Best for">{d.best_for}</Field>}
            {slots.length > 0 && (
              <Field label="Slots">
                <div className="flex flex-wrap gap-1.5">
                  {slots.map((s) => (
                    <span key={s} className="font-mono text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{`{${s}}`}</span>
                  ))}
                </div>
              </Field>
            )}
            {typeof d.recreation_prompt === 'string' && (
              <Field label="Recreation prompt (slots bound at handoff; never guessed)">
                <pre className="whitespace-pre-wrap font-mono text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700">{d.recreation_prompt}</pre>
              </Field>
            )}
            {item.evidence[0] && <Field label="Evidence">{item.evidence[0]}</Field>}
          </>
        )}

        {item.exemplar_ad_keys.length > 0 && (
          <Field label={`Exemplar ads (internal only, ${item.exemplar_ad_keys.length}) — Meta Ad Library ids, never sent to providers`}>
            <div className="flex flex-wrap gap-1.5">
              {item.exemplar_ad_keys.map((k) => {
                const url = adLibraryUrl(k);
                return url ? (
                  <a
                    key={k}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-mono text-[11px] px-1.5 py-0.5 rounded border border-slate-200 text-slate-600 hover:text-brand-teal hover:border-brand-teal/40"
                  >
                    {k} <ExternalLink size={10} />
                  </a>
                ) : (
                  <span key={k} className="font-mono text-[11px] px-1.5 py-0.5 rounded border border-slate-200 text-slate-500">{k}</span>
                );
              })}
            </div>
          </Field>
        )}

        {item.entity_class !== 'creative_pattern' && (
          <Field label="Cited by angles">{cited > 0 ? `${cited} angle${cited === 1 ? '' : 's'} cited this item` : 'not cited yet'}</Field>
        )}

        <Field label="Admin note (kept with the hide/unhide state)">
          <textarea
            id="cs-item-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => {
              if ((item.note ?? '') !== note && canManage) setItemOverride(clientUid, item.resolved_id, item.hidden, note);
            }}
            rows={2}
            disabled={!canManage}
            placeholder="Why this item is hidden, or anything the next reviewer should know"
            className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:border-brand-teal/50 disabled:bg-slate-50"
          />
        </Field>

        <div className="text-[11px] text-slate-400 font-mono">{item.embedded_text}</div>
      </div>
    </div>
  );
}
